import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  useHover,
  safePolygon,
} from '@floating-ui/react'
import { Editor } from '@tiptap/react'
import { Copy, Check, ExternalLink, Globe, SquarePen } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DocIcon } from '@/components/ui/doc-icon'
import './link-hover-card.scss'
import { useAtom } from 'jotai'
import { activeBranchAtom, workspaceIdAtom, activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { ensureInternalDocParams } from '@/utils/link-helpers'

interface LinkHoverCardProps {
  editor: Editor
  onEdit: () => void
}

export function LinkHoverCard({ editor, onEdit }: LinkHoverCardProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [linkElement, setLinkElement] = React.useState<HTMLElement | null>(null)
  const [linkData, setLinkData] = React.useState<{
    href: string
    title?: string
  } | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [isHoveringCard, setIsHoveringCard] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // The Topic model is gone; the internal-page-link picker has nothing to offer.
  const allTopicsFlat: any[] = []
  const [workspaceId] = useAtom(workspaceIdAtom)
  const [activeBranch] = useAtom(activeBranchAtom)
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)

  // Find topic by URL if it's an internal link
  const linkedTopic = React.useMemo(() => {
    if (!linkData?.href) return null

    // Check if it's an internal topic link

    // const topicMatch = linkData.href.match(/\/topics\/(.+)/)
    // if (topicMatch) {
    //   const topicId = topicMatch[1]
    //   return allTopicsFlat.find((topic) => topic.id === topicId)
    // }
    const docId = linkData.href.split('?')[1]?.split('docId=')[1]?.split('&')[0]
    if (docId) {
      return allTopicsFlat.find((topic) => topic.docId === docId)
    }

    return null
  }, [linkData, allTopicsFlat])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [
      offset(6), // Reduced offset to make it closer to the link
      flip({
        fallbackPlacements: [
          'bottom',
          'top-start',
          'top-end',
          'bottom-start',
          'bottom-end',
        ],
      }),
      shift({ padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    handleClose: safePolygon({
      blockPointerEvents: false,
      buffer: 1,
    }),
    delay: {
      open: 500,
      close: 0,
    },
    enabled: !!linkElement,
  })

  const dismiss = useDismiss(context)

  const { getFloatingProps } = useInteractions([hover, dismiss])

  React.useEffect(() => {
    if (!editor) return

    const linkRefs = new WeakMap<
      HTMLElement,
      { mouseenter: () => void; mouseleave: () => void }
    >()

    const setupLinkListeners = (linkEl: HTMLElement) => {
      if (linkRefs.has(linkEl)) return

      const handleMouseEnter = () => {
        // Clear any pending timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Get link data
        const href = linkEl.getAttribute('href') || ''
        const title = linkEl.textContent || ''

        setLinkElement(linkEl)
        setLinkData({ href, title })
        refs.setReference(linkEl)
        setIsOpen(true)
      }

      const handleMouseLeave = (event: MouseEvent) => {
        const relatedTarget = event.relatedTarget as HTMLElement | null

        // Check if we're moving to the hover card
        const isMovingToCard = relatedTarget?.closest('.link-hover-card')

        if (!isMovingToCard) {
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }

          // Delay closing to allow moving to the hover card
          timeoutRef.current = setTimeout(() => {
            if (!isHoveringCard) {
              setLinkElement(null)
              setLinkData(null)
              setIsOpen(false)
            }
          }, 500)
        }
      }

      linkEl.addEventListener('mouseenter', handleMouseEnter)
      linkEl.addEventListener('mouseleave', handleMouseLeave as EventListener)

      linkRefs.set(linkEl, {
        mouseenter: handleMouseEnter,
        mouseleave: handleMouseLeave as () => void,
      })
    }

    // Observer to detect new links
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const links = node.querySelectorAll('a[href]')
            links.forEach((link) => {
              if (link instanceof HTMLElement) {
                setupLinkListeners(link)
              }
            })
            if (node.tagName === 'A' && node.hasAttribute('href')) {
              setupLinkListeners(node)
            }
          }
        })
      })
    })

    // Setup listeners for existing links
    const existingLinks = editor.view.dom.querySelectorAll('a[href]')
    existingLinks.forEach((link) => {
      if (link instanceof HTMLElement) {
        setupLinkListeners(link)
      }
    })

    // Start observing
    observer.observe(editor.view.dom, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()

      // Clean up all link listeners
      existingLinks.forEach((link) => {
        if (link instanceof HTMLElement) {
          const handlers = linkRefs.get(link)
          if (handlers) {
            link.removeEventListener('mouseenter', handlers.mouseenter)
            link.removeEventListener(
              'mouseleave',
              handlers.mouseleave as EventListener
            )
          }
        }
      })

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [editor, refs, setIsOpen, isHoveringCard])

  const handleEdit = () => {
    setIsOpen(false)
    if (linkElement) {
      // Find the position of the link element in the editor
      const pos = editor.view.posAtDOM(linkElement, 0)
      const textLength = linkElement.textContent?.length || 0

      // Select the entire link text
      editor
        .chain()
        .focus()
        .setTextSelection({ from: pos, to: pos + textLength })
        .extendMarkRange('link')
        .run()

      // Small delay to ensure selection is set before opening editor
      setTimeout(() => {
        onEdit()
      }, 50)
    } else {
      onEdit()
    }
  }

  // Ensure internal docs link includes topicId/docId by resolving slug before navigating
  const ensureInternalDocUrl = React.useCallback(
    async (rawUrl: string) => {
      if (!workspaceId) return rawUrl
      return ensureInternalDocParams(rawUrl, workspaceId, activeBranch?.id, workspace?.slug)
    },
    [workspaceId, activeBranch?.id, workspace?.slug]
  )

  const handleOpenLink = async () => {
    if (linkData?.href) {
      // Check if it's an internal link
      if (linkData.href.startsWith('/')) {
        const finalUrl = await ensureInternalDocUrl(linkData.href)
        // Try to force save current document before navigation
        try {
          const editorAny = editor as any
          if (editorAny?.forceSaveDocument) {
            await editorAny.forceSaveDocument()
          }
        } catch (_e) {}
        window.location.href = finalUrl
      } else {
        // Open external links in new tab
        window.open(linkData.href, '_blank', 'noopener,noreferrer')
      }
    }
  }

  const handleCopyLink = async () => {
    if (linkData?.href) {
      try {
        await navigator.clipboard.writeText(linkData.href)
        setCopied(true)

        // Show success toast
        toast.success('Link copied to clipboard')

        // Close the hover card
        setIsOpen(false)

        // Reset copied state after 2 seconds
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = setTimeout(() => {
          setCopied(false)
        }, 2000)
      } catch (err) {
        console.error('Failed to copy link:', err)
        toast.error('Failed to copy link')
      }
    }
  }

  // Cleanup copy timeout on unmount
  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  if (!isOpen || !linkData || !linkElement) {
    return null
  }

  const displayTitle = linkedTopic?.title || linkData.href
  const isInternalLink = !!linkedTopic

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className='link-hover-card'
        onMouseEnter={() => {
          setIsHoveringCard(true)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
        }}
        onMouseLeave={() => {
          setIsHoveringCard(false)
          timeoutRef.current = setTimeout(() => {
            setLinkElement(null)
            setLinkData(null)
            setIsOpen(false)
          }, 300)
        }}
      >
        <div className='link-hover-card-content'>
          <div className='link-hover-card-info'>
            {isInternalLink && linkedTopic ? (
              <DocIcon topic={linkedTopic} />
            ) : (
              <Globe className='link-hover-card-icon' />
            )}
            <span className='link-hover-card-title'>{displayTitle}</span>
          </div>
          <div className='link-hover-card-actions'>
            <Button
              variant='ghost'
              size='xs'
              className='dark:hover:bg-accent!'
              onClick={handleOpenLink}
              title={isInternalLink ? 'Go to page' : 'Open link in new tab'}
            >
              <ExternalLink className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              onClick={handleCopyLink}
              title='Copy link'
              className='dark:hover:bg-accent!'
              size='xs'
            >
              {copied ? (
                <Check className='h-4 w-4' />
              ) : (
                <Copy className='h-4 w-4' />
              )}
            </Button>
            <Button
              variant='ghost'
              size='xs'
              onClick={handleEdit}
              title='Edit link'
              className='dark:hover:bg-accent!'
            >
              <SquarePen className='h-4 w-4' />
            </Button>
          </div>
        </div>
      </div>
    </FloatingPortal>
  )
}
